import { DeleteFilled, DeleteOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useTranslation } from "react-i18next";

import { BUTTON_KEYS, BUTTON_TYPE } from "@/constants/Button";
import { getModelTranslationKey } from "@/modules/model";
import { useModelDelete } from "@/modules/model-delete";

/**
 * @typedef {Object} DeleteButtonPartialProps
 * @property {import("@/hooks/queries").QueryKey[]} [invalidateKeys]
 * @property {boolean} [isIconButton=false]
 * @property {import("@/modules/model").ModelItem} model
 * @property {import("@tanstack/react-query").QueryFilters["predicate"]} [predicateRemoveKeys]
 * @property {number} [rootId]
 * @property {import("@/modules/model").ModelKey} modelKey
 * @property {() => void} [onDeleted]
 * @property {import("antd").ButtonProps} [buttonProps]
 */

/** @param {DeleteButtonPartialProps} props */
const DeleteButton = ({
  invalidateKeys,
  isIconButton = false,
  model,
  nameParam,
  predicateRemoveKeys,
  rootId,
  titleType,
  modelKey,
  onDeleted,
  buttonProps = {},
}) => {
  const { t } = useTranslation();
  const { openDelete } = useModelDelete();

  const translationKey = getModelTranslationKey(modelKey);
  const title = t(
    titleType ? `${titleType}:delete.label` : `${translationKey}:delete.label`
  );

  /**
   * Open side panel to delete the asset
   *
   * @return {void}
   */
  function onDeleteClick() {
    openDelete({
      title,
      modelKey,
      item: model,
      nameParam,
      rootId,
      invalidateKeys,
      predicateRemoveKeys,
      onDeleted,
    });
  }

  if (isIconButton) {
    return (
      <Button
        aria-label={title}
        block={false}
        icon={<DeleteFilled />}
        key={BUTTON_KEYS.DELETE}
        role="button"
        shape="circle"
        title={title}
        type={BUTTON_TYPE.TEXT}
        onClick={onDeleteClick}
        {...buttonProps}
      />
    );
  }

  return (
    <Button
      aria-label={title}
      icon={<DeleteOutlined />}
      key={BUTTON_KEYS.DELETE}
      title={title}
      type={BUTTON_TYPE.DEFAULT}
      onClick={onDeleteClick}
      {...buttonProps}
    >
      {t("global:delete")}
    </Button>
  );
};

export default DeleteButton;
